import type { CaptureResult } from 'posthog-js';

/**
 * Drop known-benign `$exception` events before they leave the browser.
 *
 * Each `DENYLIST_MATCHERS` entry below matches a class of error that is either
 * environmental (a browser quirk we can't fix) or already handled elsewhere (a
 * CSP violation whose fallback path works). Filtering at `before_send` keeps
 * these out of the PostHog dashboard and avoids consuming event quota.
 *
 * ADDING A NEW SUPPRESSION — there are two mechanisms, pick by what evidence you
 * need. If the message alone identifies the class, append a matcher to
 * `DENYLIST_MATCHERS`; its entries receive the message string and nothing else.
 * If you need stack evidence to tell foreign code from ours, write a named
 * predicate and call it from `filterKnownExceptionNoise` — see
 * `isInjectedEvalReferenceError` and `isRecorderCrossOriginSecurityError`.
 * Deliberately NOT a second array: two such rules exist today, and rule-of-three
 * says wait for the third before building structure for it.
 *
 * Every entry here silently discards an error, so scope each one as narrowly as
 * the evidence allows (equality over substring, extra clauses over fewer) and
 * pair it with a test proving each clause is load-bearing.
 *
 * Non-`$exception` events pass through unchanged (object identity preserved).
 */

/**
 * Shape of a `$exception_list` entry as it exists CLIENT-SIDE at `before_send`.
 *
 * `stacktrace` is assigned conditionally by the SDK (`err.stack && (…)`), so it —
 * and therefore `frames` — can be absent entirely. Any guard reading frames must
 * fail open when they are missing.
 */
interface RawException {
  value?: string;
  stacktrace?: { frames?: RawExceptionFrame[] };
}

/**
 * A single client-side stack frame.
 *
 * Deliberately narrow. posthog-js populates only platform/filename/function/lineno/
 * colno/in_app/chunk_id on the client; `resolved`, `resolve_failure`, `mangled_name`
 * and `context_line` are SERVER-SIDE symbolication artifacts and are always undefined
 * here — nothing in this file may key on them, however diagnostic they look in the
 * PostHog UI. Re-verified against posthog-js@1.434.2: the client frame builder still
 * emits only filename/function/lineno/colno/in_app (+platform/chunk_id), and
 * `$exception_list[].value` is the same field posthog-js's own before_send denylist
 * reads, so the shape this module keys on is load-bearing upstream.
 */
interface RawExceptionFrame {
  function?: string;
  filename?: string;
}

const DENYLIST_MATCHERS: ReadonlyArray<(message: string) => boolean> = [
  // Benign Safari/Chrome layout warning surfaced as an uncaught error.
  (m) => m.includes('ResizeObserver loop'),
  // PostHog synthesizes this when a non-Error value (e.g. a DOM Event) is
  // captured — the real callsite info is already lost by the time we see it.
  (m) => m.includes('Event captured as exception'),
  // Meta's in-app browser (Instagram/Facebook on iOS) injects a script that
  // relays page events to its native shell via the WKWebView bridge
  // (window.webkit.messageHandlers). On sessions where that bridge is absent it
  // throws "undefined is not an object (evaluating 'window.webkit.messageHandlers')".
  // The injected code (sendDataToNative / sendPageShowMessage) isn't ours — we
  // never touch the WKWebView bridge — so it's unactionable noise. Match the
  // bridge access itself: the function names live only in stack frames, which the
  // entries in THIS array can't see (each receives the message alone). The module
  // does read frames — see isInjectedEvalReferenceError below — but this class
  // needs no frame evidence, so it stays a plain message matcher.
  (m) => m.includes('webkit.messageHandlers'),
  // PostHog survey targeting evaluates a string as JS on every page load,
  // which our CSP blocks (no 'unsafe-eval'). Surveys still render via a
  // fallback path; the CSP violation is noise.
  (m) => m.includes('unsafe-eval') && m.includes('Content Security Policy'),
  // A foreign/injected script (browser extension or third-party tag) loads
  // `/static/identify.js` against our origin — a path we don't serve. Our
  // webpack chunks live under /_next/static/chunks/ and the PostHog proxy under
  // /ingest/static/; nothing references /static/identify.js anywhere in the app.
  // Its failed load surfaces as a webpack-shaped
  // "ChunkLoadError: Loading chunk identify failed.\n(error: .../static/identify.js)".
  // It isn't our code and reloading can't fetch a resource we don't serve, so
  // it's unactionable third-party noise. Match either the distinctive foreign
  // asset path or the "identify" chunk name (we have no chunk by that name); a
  // genuine app chunk failure names /_next/static/chunks/ and stays visible.
  (m) => m.includes('/static/identify.js') || m.includes('Loading chunk identify failed'),
  // Android WebView in-app browsers (Meta apps on Android) inject a relay
  // script backed by an addJavascriptInterface Java bridge. When the WebView
  // navigates or is torn down while the injected script still holds a bridge
  // reference, calls into it throw "Java object is gone" — pure in-app-browser
  // lifecycle noise, never our code (we don't touch any Java bridge).
  (m) => m.includes('Java object is gone'),
  // Same injected bridge, different failure shape: the relay wraps its native
  // calls and reports "Error invoking <method>" when the bridge is gone.
  // postMessage is the generic relay entrypoint;
  // enableDidUserTypeOnKeyboardLogging is a keyboard-telemetry hook some
  // in-app browsers inject. Matched on the full "Error invoking <method>"
  // prefix so a genuine app error like "Failed to execute 'postMessage' on
  // 'Window'" stays visible.
  (m) => m.startsWith('Error invoking postMessage'),
  (m) => m.startsWith('Error invoking enableDidUserTypeOnKeyboardLogging'),
  // Cross-origin script errors arrive as a literal "Script error." (with
  // trailing period per HTML spec; older browsers emit it without). The
  // browser strips message/source/lineno/colno/stack to prevent cross-origin
  // info leakage, so PostHog can't surface anything actionable. Almost always
  // an injected extension or content-blocker script. Match on equality, not
  // substring, so a future `throw new Error('Script error in fooBar()')`
  // isn't silently swallowed.
  (m) => m === 'Script error.' || m === 'Script error',
  // Facebook's Meta Pixel "Event Setup Tool" (Interactive Web Loader) injects
  // /signals/iwl.js into the page when an operator opens the site from Facebook
  // Events Manager (referrer eventsmanager.facebook.com). During that session
  // Facebook's own script can throw — e.g. a Graph API OAuthException when the
  // operator's Facebook token has expired. These are autocaptured by posthog-js,
  // but every frame is in /signals/iwl.js; none is our code, and they fire only for
  // the ads operator using the tool, never for real visitors. Two shapes seen in
  // prod (issues 019f8ae9…693867ea6a22 / …692ae1074d35):
  //   1. The thrown value is an IWL telemetry JSON blob; its unencoded
  //      sessions[0].scope is literally "SIGNALS_IWL" (Facebook's IWL scope marker,
  //      which never appears in our code — the sibling namespace field holds
  //      "ADS_SIGNALS"). Match that marker — stable across the varying BOOTSTRAP_*
  //      scopes and timestamps in the blob.
  (m) => m.includes('SIGNALS_IWL'),
  //   2. The downstream invariant check has type "Invariant Violation" and a value
  //      of exactly "Invalid Graph API Response" (the type is a separate field, so
  //      the value we read carries no prefix — verified against the prod event).
  //      Match on equality (like "Script error." above) so a future app error merely
  //      containing the phrase stays visible.
  (m) => m === 'Invalid Graph API Response',
  // A script injected by the visitor's browser (extension, in-app-browser relay, or
  // content blocker) references an undeclared global `_G` and throws. Not ours: `_G`
  // appears nowhere in src/, public/, or the served HTML, and the frame's claimed
  // position is impossible — WebKit reported `global code` at the document URL line 1
  // column 3, but column 3 is the "D" of "<!DOCTYPE html>" and the document's first
  // inline <script> starts at column 7805. WebKit attributes injected code with no
  // //# sourceURL to the top-level document, which is why it arrives wearing our
  // origin. Equality, not substring, so a real ReferenceError for a global merely
  // starting with "_G" stays visible (prod issue 019fa7b2…a90b2).
  (m) => m === "Can't find variable: _G",
];

/**
 * Injected-`eval` ReferenceError guard — the first class that needs stack evidence, not
 * just a message.
 *
 * "start is not defined" is too generic to denylist by message alone: minifiers do not
 * rename undeclared identifiers, so a bare `start` typo in our own source survives
 * minification and throws this exact string in Chromium. Dropping it unconditionally
 * would hide a real first-party bug.
 *
 * The `eval` frame is what makes it safe IN PRODUCTION, which is the only place it
 * matters. Our production CSP carries no 'unsafe-eval' (security/headers.ts adds it
 * only when NODE_ENV === 'development'), so our own code cannot be evaluated that way
 * there; browser extensions bypass page CSP, which is why injected code can. Staging
 * and preview builds run NODE_ENV=production and are covered too.
 * (Prod issue 019fa855…1a592.)
 *
 * Scope note, because the tempting stronger claim is false: `capture_exceptions:
 * !isDevelopment` in posthog.tsx disables AUTOMATIC autocapture in development, but
 * `posthog.init` still runs there and the manual `captureException` wrapper
 * (posthog.tsx, used by use-conversation.ts) is NOT dev-gated — unlike the logger's
 * client sink, which is. So in development, where the CSP does permit 'unsafe-eval' and
 * Next's dev webpack wraps module bodies accordingly, a manually captured error COULD
 * reach this filter with `eval` frames. Accepted: it cannot hide a production bug, and
 * it would additionally have to carry this exact message.
 *
 * `every`, never `frames[0]`: posthog-js builds the frame array via
 * Array.from(stack).reverse(), so which end holds the throwing frame is an undocumented
 * convention. Requiring EVERY frame to be eval is ordering-independent and strictly
 * safer — a mixed stack containing any of our real code can never satisfy it.
 *
 * Fails open on missing, empty, or malformed frames: without frames there is no evidence
 * of injection, and `[].every()` is vacuously true, so the emptiness check is
 * load-bearing. `Array.isArray` rather than a truthiness test because `frames` reaches
 * us through an unchecked structural cast that could lie — and posthog-js runs
 * before_send handlers with no try/catch (`i = r(i)` inline in `capture()`), so a
 * TypeError raised here would escape `posthog.capture()` and break capture entirely.
 */
function isInjectedEvalReferenceError(
  message: string,
  frames: RawExceptionFrame[] | undefined
): boolean {
  if (message !== 'start is not defined') return false;
  if (!Array.isArray(frames) || frames.length === 0) return false;
  return frames.every((frame) => frame.function === 'eval');
}

// A PostHog recorder bundle served through our /ingest proxy. posthog.tsx sets api_host
// '/ingest' and next.config.mjs rewrites /ingest/static/* to PostHog's asset host, so
// nothing first-party is ever served under this prefix. posthog-js builds the URL as
// /static/<version>/<name>.js first and falls back to /static/<name>.js?v=<version> when
// that load fails (strict_script_versioning defaults to "fallback", so one install can
// emit both shapes) or when versioning is off. <name> comes from PostHog's remote config
// (scriptConfig.script, default "lazy-recorder"; the prod event loaded "posthog-recorder");
// 1.434.2 ships recorder / recorder-v2 / lazy-recorder / posthog-recorder, all the same
// rrweb recorder, hence the permissive basename.
// Path-anchored, not origin-anchored: a foreign origin carrying this exact path is by
// construction not our code either, so that residual is accepted.
const RECORDER_SCRIPT_PATH =
  /\/ingest\/static\/(?:[^/?#]+\/)?[\w-]*recorder(?:-v\d+)?\.js(?:[?#]|$)/;

/**
 * PostHog-recorder-internal cross-origin guard — the second class that needs stack
 * evidence.
 *
 * PostHog's session recorder (rrweb fork, bundled in posthog-js) registers a `load`
 * listener on every iframe it serializes. On a later load it calls
 * ShadowDomManager.observeAttachShadow, which reads `iframe.contentWindow.Element` after
 * checking only that contentWindow/contentDocument are truthy — no try/catch. Firefox
 * returned a non-null contentDocument for a third-party iframe on the marketing homepage
 * but treated the window as cross-origin, so the read threw its cross-origin wrapper
 * SecurityError (prod issue 01a0d512…aba1; Firefox 156; 1 event / 1 user / 90 d). The
 * exception fires inside the iframe load handler, touches no page state of ours, and
 * the session's replay still recorded. It is PostHog's bug, not ours; the only action
 * available to us is to keep it out of error tracking. Suppressing the PostHog issue
 * instead would not stick: suppression is per fingerprint, and every posthog-js bump
 * moves the recorder's stack and reopens it under a new id.
 *
 * Message clause: Firefox's wrapper string. The "SecurityError: " prefix is real —
 * @posthog/core's DOMException coercer builds the value as `${name}: ${message}`
 * (unlike plain Errors, where the type is a separate field; see the Graph API entry
 * above). Chromium phrases the same failure differently ("Blocked a frame with origin
 * …") and has not been observed, so the guard stays scoped to the evidence. Prefix,
 * not equality, because the quoted property name ("Element" here) is whatever the
 * recorder touched.
 *
 * Frame clause: EVERY frame's filename must match RECORDER_SCRIPT_PATH. Our own code
 * CAN hit this exact Firefox message by reading a cross-origin frame, which is why the
 * frame clause is load-bearing and why a mixed stack stays visible.
 *
 * `in_app` is useless here and deliberately not consulted: @posthog/core sets it from
 * the filename's URL scheme alone (an http/https/blob/webpack/… allow-list, and true
 * when there is no scheme unless the filename is empty or `<anonymous>`), so any http(s)
 * script from any origin, including every vendor script we proxy, is "in app".
 *
 * Same hazards as isInjectedEvalReferenceError: `every` (never frames[0]) because the
 * array's orientation is an undocumented convention — on the prod event the throwing
 * frame was LAST; fail open on missing, empty, or malformed frames because `[].every()`
 * is vacuously true and before_send runs with no try/catch inside posthog.capture().
 * `filename` arrives through the same unchecked cast; RegExp.test would merely coerce a
 * non-string, so the typeof check is for type narrowing and to keep a future
 * .includes/.startsWith rewrite safe, not because the regex could throw.
 */
function isRecorderCrossOriginSecurityError(
  message: string,
  frames: RawExceptionFrame[] | undefined
): boolean {
  if (!message.startsWith('SecurityError: Permission denied to access property')) return false;
  if (!Array.isArray(frames) || frames.length === 0) return false;
  return frames.every(
    (frame) => typeof frame.filename === 'string' && RECORDER_SCRIPT_PATH.test(frame.filename)
  );
}

export function filterKnownExceptionNoise(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== '$exception') return event;

  const exceptionList = event.properties?.$exception_list as RawException[] | undefined;
  const firstException = exceptionList?.[0];
  const message = String(firstException?.value ?? event.properties?.$exception_message ?? '');
  const frames = firstException?.stacktrace?.frames;

  if (DENYLIST_MATCHERS.some((match) => match(message))) return null;
  if (isInjectedEvalReferenceError(message, frames)) return null;
  if (isRecorderCrossOriginSecurityError(message, frames)) return null;
  return event;
}
